import { EntityBuilder } from '../../domain/entity/builder'
import { Num, Utils } from '../common'
import { logger } from '../common/logger'
import { DOMEvents, DOMUtils } from '../dom'
import { Queue } from '../queue'
import { IXDStorageRequest } from '../storage/global/request'
import { IXDResolver } from './actions'
import { IXDResponse, XDResponse } from './response'

export class XDTransmitter {
    public static iFrameId = 'fem-xd'
    public static timeoutMs = Num.secToMs // one second
    // public static timeoutMs = 15000 // minimum viable for initial (on domcontentloaded) IE 11 VM

    public xdOrigin: string

    protected iFrame: Window
    protected ready: Promise<void>
    protected queue = new Queue<IXDResolver<any>>()

    constructor(protected xdSrc: string) {
        this.initIFrame()
        this.xdOrigin = new URL(this.xdSrc).origin
    }

    /**
     * Sends cross-domain request.
     *
     * @param {XDStorageRequest} req
     * @param {number} timeoutMs
     */
    public async request<T>(req: IXDStorageRequest<T>, timeoutMs: number = XDTransmitter.timeoutMs): Promise<T> {
        const resolver = { id: req.id } as IXDResolver<T>
        await this.ready
        const p = Utils.promiseTimeout<T>(
            new Promise((resolve, reject) => {
                resolver.resolve = resolve
                try {
                    logger('debug', `XDTransmitter iFrame.postMessage ${req.action}`,
                    `request on IFrame ${this.xdSrc} with key ${req.key}`,
                    `req.data = ${JSON.stringify(req.data)}`)
                    this.iFrame.postMessage(req.send(), this.xdOrigin)
                } catch (e) {
                    logger('error', `XDTransmitter Exception ${e} during ${req.action}`,
                    `request on IFrame ${this.xdSrc} with key ${req.key}`)
                    reject(e)
                }
            }), timeoutMs)

        this.queue.push(resolver)
        try {
            return await p
        } catch (e) {
            this.queue.remove(resolver)
            throw e
        }
    }

    /**
     * Creates and loads iFrame, passing origin in hash.
     */
    protected initIFrame() {
        this.ready = new Promise(async (resolve) => {
            await DOMUtils.ready
            let el: HTMLIFrameElement = document.getElementById(XDTransmitter.iFrameId) as HTMLIFrameElement
            if (!el) {
                el = document.createElement(DOMUtils.tagNames.iFrame) as HTMLIFrameElement
                el.id = XDTransmitter.iFrameId
                el.src = `${(new URL(this.xdSrc)).protocol}//${this.xdSrc.replace(/^(?:.*\/\/)?([^\/]+.*)$/, '$1')}`
                    + `#${encodeURIComponent(document.location.href.replace(/^(.*\/\/[^\/]+).*$/, '$1'))}`
                DOMUtils.setVis(el, false)
                document.body.appendChild(el)
            }

            let ready = false
            try {
                ready =
                    el.contentWindow.document.readyState === 'complete'
                    // NOTE: Chome pre-populates any iframe with 'about:blank'
                    && el.contentWindow.location.href !== 'about:blank'
            } catch {
                // IE 11 :(
            }
            if (ready) {
                this.iFrame = el.contentWindow
                resolve()
            } else {
                el.addEventListener(DOMEvents.load, () => {
                    this.iFrame = el.contentWindow
                    resolve()
                })
            }
            window.addEventListener(DOMEvents.message, this.onMessage.bind(this))
        })
    }

    /**
     * Handles message responses from cross domain receiver.
     */
    protected onMessage(e: MessageEvent) {
        if (e.origin !== this.xdOrigin) {
            return
        }

        const res: IXDResponse = EntityBuilder.buildOne<IXDResponse>(XDResponse, e.data)
        if (res.validate()) {
            if (this.queue.peek() && res.replyTo === this.queue.peek().id) {
                this.queue.shift().resolve(res.data)
                // resolve enqueued and ready
                while (this.queue.peek() && this.queue.peek().data !== undefined) {
                    const resolver = this.queue.shift()
                    resolver.resolve(resolver.data || undefined)
                }
            } else {
                const enqueued = this.queue.find((r) => res.replyTo === r.id)
                if (enqueued) {
                    enqueued.data = res.data || ''
                }
            }
        }
    }
}
